import { Box, Heading, Panel } from '@hover/blueprint';
import React, { useContext, useEffect, useState } from 'react';
import { animated, useTrail } from 'react-spring';

import { ExpandButton } from 'src/modules/designStudio/components/DesignMenu/ColorPicker/ExpandButton';
import { ExpandHeader } from 'src/modules/designStudio/components/DesignMenu/ColorPicker/ExpandHeader';
import { UIKind } from 'src/modules/designStudio/components/DesignStudio/UILayer/types';
import type { MenuItemType } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import { TextureColorGroupLabelColors } from 'src/modules/designStudio/constants/hardCodedData/textureData';
import { animationConfig } from 'src/modules/designStudio/helpers/animationConfig';
import { uiLayerContext } from 'src/modules/designStudio/providers';

import { ImageSelectionButton } from '../../ImageSelectionButton';
import { PillList } from '../PillList';

type Item = {
  colorGroup?: string;
  name?: string;
  thumbnail?: string;
  textureId: number;
};

interface ColorPalletProps {
  items: Item[];
  selectedId: number;
  onClick: (textureId: number) => void;
}

interface ColorIconListProps {
  items: Item[];
  title: string;
  groups: string[];
  selectedMenuItem: MenuItemType;
  onClick: (textureId: number) => void;
  colorGroupLabelColors?: { [key: string]: string };
}

const ColorPallet = ({ items, onClick, selectedId }: ColorPalletProps) => {
  const trails = useTrail(items.length, animationConfig.menuItemAnimation);

  return (
    <Panel
      display="grid"
      gridArea={100}
      gridRowGap={400}
      gridTemplateColumns="auto auto auto"
      justifyItems="center"
    >
      {trails.map((style, index) => (
        <animated.div key={items[index].textureId} style={style}>
          <Box key={items[index].textureId} padding={100}>
            <ImageSelectionButton
              alt={items[index].name || 'texture'}
              height={88}
              isSelected={items[index].textureId === selectedId}
              label={`texture-${items[index].textureId}`}
              onClick={() => onClick(items[index].textureId)}
              shape="circle"
              src={items[index].thumbnail}
              width={88}
            />
          </Box>
        </animated.div>
      ))}
    </Panel>
  );
};

const TexturePicker = ({
  title,
  groups,
  items,
  selectedMenuItem,
  onClick,
  colorGroupLabelColors = TextureColorGroupLabelColors,
}: ColorIconListProps) => {
  const [selectedColorGroup, setColorGroup] = useState<string | undefined>();
  const [hasFocus, setHasFocus] = useState<boolean>(false);
  const [isPopoverOpen, setPopoverOpen] = useState<boolean>(false);
  const [lastPopoverOnClose, setLastPopoverOnClose] = useState<number>(
    Date.now(),
  );
  const { kind } = useContext(uiLayerContext);

  useEffect(() => {
    const defaultGroup = selectedMenuItem.colorGroup || groups[0];
    setColorGroup(defaultGroup);
  }, [groups, selectedMenuItem.colorGroup]);

  const debouncedPopoverToggle = () => {
    if (Date.now() - lastPopoverOnClose > 150) {
      setPopoverOpen(!isPopoverOpen);
    }
  };

  const expandHeader = (
    <ExpandHeader
      groupLabelColors={colorGroupLabelColors}
      groups={groups}
      isOpen={isPopoverOpen}
      onClick={debouncedPopoverToggle}
      onClose={() =>
        setPopoverOpen(() => {
          setLastPopoverOnClose(Date.now());
          return false;
        })
      }
      selectedGroup={selectedColorGroup || groups[0]}
      setColorGroup={setColorGroup}
    />
  );

  const expandButton =
    kind === UIKind.LANDSCAPE ? (
      <ExpandButton onClick={debouncedPopoverToggle} />
    ) : undefined;

  const filteredItems = selectedColorGroup
    ? items.filter(item => item.colorGroup === selectedColorGroup)
    : items;

  return (
    <Box
      display="block"
      flexDirection="column"
      marginBottom={600}
      onBlur={() => setHasFocus(false)}
      onFocus={() => setHasFocus(true)}
    >
      <Box flexDirection="column">
        {groups.length > 0 ? (
          <PillList
            enableScrollIntoView={hasFocus}
            expandButton={expandButton}
            expandHeader={expandHeader}
            header={title}
            items={groups}
            onClick={setColorGroup}
            selected={selectedColorGroup}
            wrapItems={false}
          />
        ) : (
          <Heading marginBottom={400} size={200}>
            {title}
          </Heading>
        )}
        <ColorPallet
          items={filteredItems}
          onClick={onClick}
          selectedId={selectedMenuItem.textureId}
        />
      </Box>
    </Box>
  );
};

export { TexturePicker };
